import { Box, Title, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { FormElementWithLocaleFragment, FormsAnswerFragment } from 'generated/resources/graphql'

import { ElementPreview } from 'modules/forms/components/preview/ElementPreview'
import { Element } from 'modules/forms/lib/Element'
import { DateElement } from 'modules/forms/lib/elements/DateElement'
import { DropdownElement } from 'modules/forms/lib/elements/DropdownElement'
import { MultipleChoiceElement } from 'modules/forms/lib/elements/MultipleChoiceElement'
import { NumberElement } from 'modules/forms/lib/elements/NumberElement'
import { SingleChoiceElement } from 'modules/forms/lib/elements/SingleChoiceElement'
import { TextElement } from 'modules/forms/lib/elements/TextElement'
import { UrlElement } from 'modules/forms/lib/elements/UrlElement'
import { Answer } from 'modules/forms/lib/types'
import type { WizardData } from 'modules/procurement/components/purchaseRequests/NewRequestModal/types'

interface CustomFormSectionStepProps {
  elements: FormElementWithLocaleFragment[]
  sectionTitle?: string
  sectionDescription?: string | null
}

const CustomFormSectionStep: React.FC<
  WizardStepRenderParams<WizardData> & CustomFormSectionStepProps
> = ({ data, onDataChanged, elements, sectionTitle, sectionDescription }) => {
  const createElement = (element: FormElementWithLocaleFragment): Element => {
    const baseOptions = {
      mandatory: element.mandatory,
      description: element.description || '',
    }

    switch (element.type) {
      case 'text':
        return TextElement.create({
          label: element.label,
          options: baseOptions,
        })

      case 'number':
        return NumberElement.create({
          label: element.label,
          options: baseOptions,
        })

      case 'select':
        const singleChoiceElement = SingleChoiceElement.create({
          label: element.label,
          options: baseOptions,
        })

        element.options.forEach((option) => {
          singleChoiceElement.addOption({
            id: option.id,
            label: option.label,
            value: option.value,
            isCorrect: option.isCorrect,
          })
        })
        return singleChoiceElement

      case 'multiselect':
        const multipleChoiceElement = MultipleChoiceElement.create({
          label: element.label,
          options: baseOptions,
        })
        element.options.forEach((option) => {
          multipleChoiceElement.addOption({
            id: option.id,
            label: option.label,
            value: option.value,
            isCorrect: option.isCorrect,
          })
        })
        return multipleChoiceElement

      case 'url':
        return UrlElement.create({
          label: element.label,
          options: baseOptions,
        })
      case 'dropdownsingle':
        const dropdownElement = DropdownElement.create({
          label: element.label,
          options: baseOptions,
        })

        element.options.forEach((option) => {
          dropdownElement.addOption({
            id: option.id,
            label: option.label,
            value: option.value,
            isCorrect: option.isCorrect,
          })
        })
        return dropdownElement

      case 'date':
        return DateElement.create({
          label: element.label,
          options: baseOptions,
        })

      default:
        return TextElement.create({
          label: element.label,
          options: baseOptions,
        })
    }
  }

  const renderElement = (element: FormElementWithLocaleFragment) => {
    const createdElement = createElement(element)

    // Set the current answer if it exists
    const currentAnswer = data.formAnswer[element.id]
    if (currentAnswer) {
      createdElement.answer = {
        answer: currentAnswer.answer || '',
        options: currentAnswer.elementOptionIds ?? undefined,
      }
    }

    const handleAnswer = (answer: Answer) => {
      // TODO: Implement properly on submission, ignore for the time being.
      const answerFragment: FormsAnswerFragment = {
        id: `temp_${element.id}_${Date.now()}`,
        answer: answer.answer,
        comment: answer.comment,
        elementId: element.id,
        elementOptionIds: answer.options,
        membership: null,
      }

      onDataChanged({
        ...data,
        formAnswer: { ...data.formAnswer, [element.id]: answerFragment },
      })
    }

    return (
      <ElementPreview
        key={element.id}
        element={createdElement}
        mode="answer"
        handleAnswer={handleAnswer}
        showElementName={false}
        titleSize="small"
      />
    )
  }

  return (
    <Box gap="s24" position="relative">
      {sectionTitle && <Title title={sectionTitle} description={sectionDescription} />}
      <Box gap="s32" paddingRight="s24">
        {elements.map(renderElement)}
      </Box>
    </Box>
  )
}

export default CustomFormSectionStep
